import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ThemeContext from '../contexts/ThemeContext';
import i18n from '../i18n';
import Icon from './Icon';
import Spinner from './Spinner';
import { getAttachment, getAttachmentDownloadUrl, deleteAttachment } from '../services/prebookService';
import { formatFileSize, sanitizeFilename, splitFileName } from '../utils/formatters';
import TruncatedFileName from './TruncatedFileName';
import {
  ATTACHMENT_STATUS,
  ATTACHMENT_POLLING_INTERVAL,
} from '../config/attachmentConfig';

const AttachmentContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: ${({ colors }) => colors.backgroundSecondary || '#f6f8fb'};
  border-radius: 12px;
  border: 1px solid ${({ colors }) => colors.borderLight || 'rgba(69, 68, 107, 0.15)'};
  margin: 8px 0;
`;

const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: ${({ colors, status }) => {
    const danger = colors.errorColor || '#ea7e8f';
    const warning = colors.warningColor || '#f59e0b';
    const dangerBg = danger.startsWith('#') ? `${danger}1A` : 'rgba(234, 126, 143, 0.1)';
    const warningBg = warning.startsWith('#') ? `${warning}1A` : 'rgba(255, 193, 7, 0.1)';
    if (status === ATTACHMENT_STATUS.CONVERSION_FAILED) return dangerBg;
    if (status === ATTACHMENT_STATUS.CONVERTING || status === ATTACHMENT_STATUS.PENDING) return warningBg;
    return `${colors.tulkaMainColor || '#46446F'}1A`;
  }};
  border-radius: 8px;
  margin-right: 16px;
`;

const FileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledFileName = styled(TruncatedFileName)`
  font-size: 14px;
  font-weight: 600;
  color: ${({ colors }) => colors.twilight || '#46446F'};
`;

const FileSize = styled.span`
  font-size: 12px;
  color: ${({ colors }) => colors.textSecondary || 'rgba(69, 68, 107, 0.6)'};
  margin-top: 4px;
`;

const StatusText = styled.span`
  font-size: 12px;
  color: ${({ colors, status }) => {
    if (status === ATTACHMENT_STATUS.CONVERSION_FAILED) return colors.errorColor || '#ea7e8f';
    if (status === ATTACHMENT_STATUS.CONVERTING || status === ATTACHMENT_STATUS.PENDING) return colors.warningColor || '#f59e0b';
    return colors.textSecondary || 'rgba(69, 68, 107, 0.6)';
  }};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background-color: ${({ colors, variant }) =>
    variant === 'danger' ? (colors.errorColor || '#ea7e8f') : (colors.tulkaMainColor || '#46446F')
  };
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background-color: transparent;
  border: 1px solid ${({ colors }) => colors.borderLight || 'rgba(69, 68, 107, 0.15)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ colors }) => {
      const danger = colors.errorColor || '#ea7e8f';
      return danger.startsWith('#') ? `${danger}1A` : 'rgba(234, 126, 143, 0.1)';
    }};
    border-color: ${({ colors }) => colors.errorColor || '#ea7e8f'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ErrorText = styled.span`
  color: ${({ colors }) => colors.errorColor || '#ea7e8f'};
  font-size: 14px;
`;

const NoAttachmentText = styled.span`
  font-size: 14px;
  color: ${({ colors }) => colors.textSecondary || 'rgba(69, 68, 107, 0.6)'};
  font-style: italic;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmDialogContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
`;

const ConfirmDialogTitle = styled.h3`
  margin: 0 0 16px 0;
  color: ${({ colors }) => colors.twilight || '#46446F'};
  font-size: 18px;
`;

const ConfirmDialogText = styled.p`
  margin: 0 0 24px 0;
  color: ${({ colors }) => colors.textSecondary || 'rgba(69, 68, 107, 0.6)'};
  font-size: 14px;
`;

const ConfirmDialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: transparent;
  border: 1px solid ${({ colors }) => colors.borderLight || 'rgba(69, 68, 107, 0.15)'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ colors }) => colors.twilight || '#46446F'};

  &:hover {
    background-color: ${({ colors }) => colors.backgroundSecondary || '#f6f8fb'};
  }
`;

const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 1001;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PreviewTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: baseline;
  max-width: calc(100% - 60px);
  min-width: 0;
`;

const PreviewTitleBase = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreviewTitleExt = styled.span`
  white-space: nowrap;
  flex-shrink: 0;
`;

const PreviewCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  border-radius: 8px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`;

/**
 * Get status display text
 */
const getStatusText = (status, conversionError) => {
  switch (status) {
    case ATTACHMENT_STATUS.PENDING:
      return i18n('attachment_status_pending') || 'Processing...';
    case ATTACHMENT_STATUS.CONVERTING:
      return i18n('attachment_status_converting') || 'Converting to PDF...';
    case ATTACHMENT_STATUS.CONVERSION_FAILED: {
      // Sanitize conversionError to prevent XSS - limit length and remove HTML
      const safeError = conversionError ? sanitizeFilename(conversionError, 200) : null;
      return safeError || i18n('attachment_status_conversion_failed') || 'Conversion failed';
    }
    case ATTACHMENT_STATUS.READY:
      return null; // Don't show status for ready
    default:
      return null;
  }
};

/**
 * Get icon based on status
 */
const getStatusIcon = (status, colors) => {
  switch (status) {
    case ATTACHMENT_STATUS.CONVERSION_FAILED:
      return (
        <Icon
          icon="error"
          iconLib="material"
          iconColor={colors.errorColor || '#ea7e8f'}
          iconSize={24}
        />
      );
    default:
      return (
        <Icon
          icon="description"
          iconLib="material"
          iconColor={colors.tulkaMainColor || '#46446F'}
          iconSize={24}
        />
      );
  }
};

const AttachmentPreview = ({
  prebookingId,
  showIfEmpty,
  isCustomer,
  onDeleted,
}) => {
  const { colors } = useContext(ThemeContext);

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isMountedRef = useRef(true);
  const statusRef = useRef(null);
  const dialogRef = useRef(null);
  const previewModalRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const deleteButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const previewLastFocusedRef = useRef(null);

  const isAttachmentEnabled = true;

  // Fetch attachment data
  const fetchAttachment = useCallback(async () => {
    if (!prebookingId || !isAttachmentEnabled) {
      if (isMountedRef.current) setLoading(false);
      return;
    }

    try {
      const response = await getAttachment(prebookingId);
      if (isMountedRef.current) {
        if (response) {
          // Handle both response formats: direct object or wrapped in attachment property
          const attachmentData = response.attachment || response;
          setAttachment(attachmentData);
          setError(null);
        } else {
          setAttachment(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch attachment:', err);
      if (isMountedRef.current) {
        // Handle specific error codes
        const status = err.status || err.response?.status;
        if (status === 404) {
          setAttachment(null);
          setError(null);
        } else {
          setError(i18n('attachment_fetch_error') || 'Failed to load attachment info');
        }
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [prebookingId, isAttachmentEnabled]);

  useEffect(() => {
    statusRef.current = attachment?.status || null;
  }, [attachment?.status]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchAttachment();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchAttachment]);

  // Polling for conversion status using setTimeout to prevent race conditions
  useEffect(() => {
    let timeoutId = null;

    const shouldPoll = () => {
      const status = statusRef.current;
      return status === ATTACHMENT_STATUS.PENDING || status === ATTACHMENT_STATUS.CONVERTING;
    };

    const poll = async () => {
      if (!isMountedRef.current) return;
      await fetchAttachment();
      // Schedule next poll only if still mounted and should still poll
      if (isMountedRef.current && shouldPoll()) {
        timeoutId = setTimeout(poll, ATTACHMENT_POLLING_INTERVAL);
      }
    };

    if (shouldPoll()) {
      timeoutId = setTimeout(poll, ATTACHMENT_POLLING_INTERVAL);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchAttachment]);

  const handlePreview = async () => {
    if (!prebookingId || previewLoading) return;

    // Check if attachment is ready for preview
    if (attachment?.status !== ATTACHMENT_STATUS.READY) {
      if (attachment?.status === ATTACHMENT_STATUS.CONVERTING || attachment?.status === ATTACHMENT_STATUS.PENDING) {
        setError(i18n('attachment_conversion_in_progress') || 'File is being converted. Please wait...');
      } else if (attachment?.status === ATTACHMENT_STATUS.CONVERSION_FAILED) {
        setError(i18n('attachment_conversion_failed_preview') || 'Cannot preview - conversion failed. Please upload a different file.');
      }
      return;
    }

    try {
      setPreviewLoading(true);
      setError(null);
      previewLastFocusedRef.current = document.activeElement;
      const response = await getAttachmentDownloadUrl(prebookingId);

      if (response?.downloadUrl) {
        setPreviewUrl(response.downloadUrl);
        setShowPreviewModal(true);
      } else {
        throw new Error('No preview URL received');
      }
    } catch (err) {
      console.error('Failed to load attachment preview:', err);
      // Handle specific error codes
      const status = err.status || err.response?.status;
      if (status === 409) {
        setError(i18n('attachment_conversion_in_progress') || 'File is being converted. Please wait...');
      } else if (status === 422) {
        setError(i18n('attachment_conversion_failed_preview') || 'Cannot preview - conversion failed.');
      } else if (status === 410) {
        setError(i18n('attachment_deleted') || 'Attachment has been deleted.');
        setAttachment(null);
      } else {
        setError(i18n('attachment_preview_error') || 'Failed to load attachment preview');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = useCallback(() => {
    setShowPreviewModal(false);
    setPreviewUrl(null);
    if (previewLastFocusedRef.current && typeof previewLastFocusedRef.current.focus === 'function') {
      previewLastFocusedRef.current.focus();
    }
  }, []);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  useEffect(() => {
    if (!showDeleteConfirm) {
      if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === 'function') {
        lastFocusedRef.current.focus();
      }
      return undefined;
    }

    lastFocusedRef.current = document.activeElement;
    const focusable = dialogRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleDeleteCancel();
      }
      if (event.key === 'Tab' && focusable && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === 'function') {
        lastFocusedRef.current.focus();
      }
    };
  }, [showDeleteConfirm, handleDeleteCancel]);

  // Preview modal keyboard handling
  useEffect(() => {
    if (!showPreviewModal) return undefined;

    const focusable = previewModalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClosePreview();
      }
      if (event.key === 'Tab' && focusable && focusable.length > 0) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showPreviewModal, handleClosePreview]);

  const handleDeleteConfirm = async () => {
    if (!prebookingId || deleting) return;

    try {
      setDeleting(true);
      setError(null);
      await deleteAttachment(prebookingId);

      if (isMountedRef.current) {
        setAttachment(null);
        setShowDeleteConfirm(false);
        if (onDeleted) {
          onDeleted();
        }
      }
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      if (isMountedRef.current) {
        const status = err.status || err.response?.status;
        if (status === 403) {
          setError(i18n('attachment_delete_forbidden') || 'You do not have permission to delete this attachment.');
        } else if (status === 404) {
          setAttachment(null);
          setError(null);
        } else {
          setError(i18n('attachment_delete_error') || 'Failed to delete attachment');
        }
        setShowDeleteConfirm(false);
      }
    } finally {
      if (isMountedRef.current) {
        setDeleting(false);
      }
    }
  };

  // Don't render if feature is not enabled for user's country
  if (!isAttachmentEnabled) {
    return null;
  }

  // Don't render if prebookingId is missing
  if (!prebookingId) {
    return null;
  }

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner size={24} />
      </LoadingContainer>
    );
  }

  if (error && !attachment) {
    return (
      <AttachmentContainer colors={colors}>
        <ErrorText colors={colors}>{error}</ErrorText>
      </AttachmentContainer>
    );
  }

  // If no attachment and we shouldn't show empty state
  if (!attachment && !showIfEmpty) {
    return null;
  }

  if (!attachment) {
    return (
      <AttachmentContainer colors={colors}>
        <NoAttachmentText colors={colors}>
          {i18n('no_attachment') || 'No document attached'}
        </NoAttachmentText>
      </AttachmentContainer>
    );
  }

  // Sanitize filename to prevent XSS
  const safeFilename = sanitizeFilename(attachment.filename);
  const { baseName: previewBaseName, extension: previewExtension } = splitFileName(safeFilename);
  const status = attachment.status || ATTACHMENT_STATUS.READY;
  const canDownload = attachment.canDownload === true || status === ATTACHMENT_STATUS.READY;
  const statusText = getStatusText(status, attachment.conversionError);
  // Handle both fileSize and size property names
  const fileSize = attachment.fileSize || attachment.size;

  return (
    <>
      <AttachmentContainer colors={colors}>
        <FileIcon colors={colors} status={status}>
          {getStatusIcon(status, colors)}
        </FileIcon>
        <FileInfo>
          <StyledFileName colors={colors} filename={safeFilename} />
          {fileSize && (
            <FileSize colors={colors}>{formatFileSize(fileSize)}</FileSize>
          )}
          {statusText && (
            <StatusText colors={colors} status={status}>
              {status === ATTACHMENT_STATUS.CONVERTING || status === ATTACHMENT_STATUS.PENDING ? (
                <Spinner size={8} />
              ) : null}
              {statusText}
            </StatusText>
          )}
          {error && (
            <StatusText colors={colors} status={ATTACHMENT_STATUS.CONVERSION_FAILED}>
              {error}
            </StatusText>
          )}
        </FileInfo>
        <ButtonGroup>
          <ActionButton
            colors={colors}
            onClick={handlePreview}
            disabled={previewLoading || !canDownload}
            title={!canDownload ? (i18n('attachment_not_ready') || 'File not ready for preview') : ''}
            aria-label={previewLoading ? (i18n('loading_preview') || 'Loading preview...') : (i18n('preview') || 'Preview')}
          >
            {previewLoading ? (
              <Spinner size={16} color="white" />
            ) : (
              <>
                <Icon
                  icon="visibility"
                  iconLib="material"
                  iconColor="white"
                  iconSize={18}
                  style={{ marginRight: 8 }}
                />
                {i18n('preview') || 'Preview'}
              </>
            )}
          </ActionButton>
          {isCustomer && (
            <IconButton
              colors={colors}
              onClick={handleDeleteClick}
              disabled={deleting}
              title={i18n('delete_attachment') || 'Delete attachment'}
              aria-label={i18n('delete_attachment') || 'Delete attachment'}
            >
              <Icon
                icon="delete"
                iconLib="material"
                iconColor={colors.errorColor || '#ea7e8f'}
                iconSize={20}
              />
            </IconButton>
          )}
        </ButtonGroup>
      </AttachmentContainer>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          onClick={handleDeleteCancel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-attachment-dialog-title"
        >
          <ConfirmDialogContent onClick={(e) => e.stopPropagation()} ref={dialogRef}>
            <ConfirmDialogTitle colors={colors} id="delete-attachment-dialog-title">
              {i18n('delete_attachment_title') || 'Delete Attachment?'}
            </ConfirmDialogTitle>
            <ConfirmDialogText colors={colors}>
              {i18n('delete_attachment_confirm') || `Are you sure you want to delete "${safeFilename}"? This action cannot be undone.`}
            </ConfirmDialogText>
            <ConfirmDialogButtons>
              <CancelButton colors={colors} onClick={handleDeleteCancel} ref={cancelButtonRef}>
                {i18n('cancel') || 'Cancel'}
              </CancelButton>
              <ActionButton
                colors={colors}
                variant="danger"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                ref={deleteButtonRef}
              >
                {deleting ? (
                  <Spinner size={16} color="white" />
                ) : (
                  i18n('delete') || 'Delete'
                )}
              </ActionButton>
            </ConfirmDialogButtons>
          </ConfirmDialogContent>
        </ConfirmDialog>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewUrl && (
        <PreviewModal
          ref={previewModalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="preview-modal-title"
          onClick={handleClosePreview}
        >
          <PreviewHeader onClick={(e) => e.stopPropagation()}>
            <PreviewTitle id="preview-modal-title" title={safeFilename}>
              <PreviewTitleBase>{previewBaseName}</PreviewTitleBase>
              {previewExtension && <PreviewTitleExt>{previewExtension}</PreviewTitleExt>}
            </PreviewTitle>
            <PreviewCloseButton
              onClick={handleClosePreview}
              aria-label={i18n('close') || 'Close'}
            >
              <Icon
                icon="close"
                iconLib="material"
                iconColor="white"
                iconSize={24}
              />
            </PreviewCloseButton>
          </PreviewHeader>
          <PreviewContent onClick={(e) => e.stopPropagation()}>
            {attachment?.contentType?.startsWith('image/') ? (
              <PreviewImage
                src={previewUrl}
                alt={safeFilename}
              />
            ) : (
              <PreviewIframe
                src={`${previewUrl}#toolbar=0&navpanes=0`}
                title={safeFilename}
              />
            )}
          </PreviewContent>
        </PreviewModal>
      )}
    </>
  );
};

AttachmentPreview.propTypes = {
  /** The prebooking ID to fetch attachment for. If null/undefined, component renders nothing. */
  prebookingId: PropTypes.string,
  /** Whether to show "No document attached" message when there's no attachment */
  showIfEmpty: PropTypes.bool,
  /** Whether the current user is the customer (owner) - enables delete functionality */
  isCustomer: PropTypes.bool,
  /** Callback when attachment is deleted */
  onDeleted: PropTypes.func,
};

AttachmentPreview.defaultProps = {
  prebookingId: undefined,
  showIfEmpty: false,
  isCustomer: false,
  onDeleted: undefined,
};

export default AttachmentPreview;
